import React, { useContext, useState, useEffect } from 'react';
import { useLocation, useNavigate, Link } from 'react-router-dom';
import {
  LayoutDashboard, FolderKanban, Building2, Users, Shield,
  FileCheck, Award, MessageSquare, ChevronDown, LogOut,
  Menu, X, ChevronRight, Globe, ClipboardList, Briefcase
} from 'lucide-react';
import { UserContext } from '../../contexts/UserContext';
import api from '../../services/apiService';

const OEMLayout = ({ children }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useContext(UserContext);
  const [teamData, setTeamData] = useState(null);
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [projects, setProjects] = useState([]);
  const [selectedProject, setSelectedProject] = useState(null);
  const [showProjectMenu, setShowProjectMenu] = useState(false);

  useEffect(() => {
    fetchTeamData();
    fetchProjects();
  }, []);

  const fetchTeamData = async () => {
    try {
      const response = await api.get('/oem/dashboard/team/');
      setTeamData(response.data);
    } catch (err) {
      console.error('Error fetching team data:', err);
    }
  };

  const fetchProjects = async () => {
    try {
      const response = await api.get('/oem/projects/');
      setProjects(response.data);
      // Set first project as default if available
      if (response.data.length > 0 && !selectedProject) {
        setSelectedProject(response.data[0]);
      }
    } catch (err) {
      console.error('Error fetching projects:', err);
    }
  };

  const handleProjectSelect = (project) => {
    setSelectedProject(project);
    setShowProjectMenu(false);
    // Navigate to project details
    navigate(`/oem/projects/${project.id}`);
  };

  // Navigation items
  const navItems = [
    { path: '/oem', label: 'Dashboard', icon: LayoutDashboard, exact: true },
    { path: '/oem/projects', label: 'Projects', icon: FolderKanban },
    { path: '/oem/companies', label: 'Companies', icon: Building2 },
    { path: '/oem/users', label: 'Users', icon: Users },
    { path: '/oem/authorization-levels', label: 'Auth Levels', icon: Shield },
    { path: '/oem/nominations', label: 'Nominations', icon: FileCheck },
    { path: '/oem/work-permits', label: 'Work Permits', icon: ClipboardList },
    { path: '/oem/work-scopes', label: 'Work Scopes', icon: Briefcase },
    { path: '/oem/certificates', label: 'Certificates', icon: Award },
    { path: '/oem/toolboxes', label: 'Toolbox Talks', icon: MessageSquare },
  ];

  // Get current page info
  const getCurrentPage = () => {
    const path = location.pathname;

    if (path === '/oem') return { title: 'Dashboard', subtitle: 'Overview & Statistics' };
    if (path.startsWith('/oem/projects/create')) return { title: 'Create Project', subtitle: 'New OEM Project' };
    if (path.startsWith('/oem/projects/')) return { title: 'Project Details', subtitle: 'Project Management' };
    if (path.startsWith('/oem/projects')) return { title: 'Projects', subtitle: 'OEM Projects Management' };
    if (path.startsWith('/oem/companies')) return { title: 'Companies', subtitle: 'Partner Companies' };
    if (path.startsWith('/oem/users')) return { title: 'Users', subtitle: 'Workforce Management' };
    if (path.startsWith('/oem/authorization-levels')) return { title: 'Authorization Levels', subtitle: 'Access Requirements' };
    if (path.startsWith('/oem/nominations/create')) return { title: 'Create Nomination', subtitle: 'New Nomination Request' };
    if (path.startsWith('/oem/nominations/')) return { title: 'Nomination Details', subtitle: 'Nomination Review' };
    if (path.startsWith('/oem/nominations')) return { title: 'Nominations', subtitle: 'Nomination Requests' };
    if (path.startsWith('/oem/work-permits')) return { title: 'Work Permits', subtitle: 'Work Permit Management' };
    if (path.startsWith('/oem/work-scopes')) return { title: 'Work Scopes', subtitle: 'Work Scope Definitions' };
    if (path.startsWith('/oem/certificates')) return { title: 'Certificates', subtitle: 'Certificate Management' };
    if (path.startsWith('/oem/toolboxes')) return { title: 'Toolbox Talks', subtitle: 'Safety Briefings' };

    return { title: 'OEM Portal', subtitle: 'Management System' };
  };

  const currentPage = getCurrentPage();

  const isActive = (item) => {
    if (item.exact) {
      return location.pathname === item.path;
    }
    return location.pathname.startsWith(item.path);
  };

  // Get user role display
  const getUserRole = () => {
    if (user?.isSuperuser) return 'Superuser';
    if (teamData?.my_roles?.length > 0) {
      const role = teamData.my_roles[0];
      return role.role_display || role.role;
    }
    return 'Admin';
  };

  // Get organization/project info
  const getOrgInfo = () => {
    if (teamData?.organization_name) {
      return teamData.organization_name;
    }
    return 'OEM Portal';
  };

  const handleLogout = async () => {
    await logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-slate-50 flex">
      {/* Left Sidebar */}
      <aside className={`hidden lg:flex flex-col bg-white border-r border-slate-200 transition-all duration-300 ${sidebarCollapsed ? 'w-16' : 'w-64'}`}>
        {/* Logo/Brand */}
        <div className="h-14 flex items-center justify-between px-4 border-b border-slate-100">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2">
              <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                <span className="text-white font-black text-sm">N</span>
              </div>
              <span className="font-black text-slate-900 text-sm">Nordex</span>
            </div>
          )}
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-600 transition-colors"
          >
            <ChevronRight className={`h-4 w-4 transition-transform ${sidebarCollapsed ? '' : 'rotate-180'}`} />
          </button>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
          {navItems.map((item) => {
            const Icon = item.icon;
            const active = isActive(item);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                  active
                    ? 'bg-orange-500 text-white shadow-lg shadow-orange-500/20'
                    : 'text-slate-600 hover:bg-slate-100 hover:text-slate-900'
                } ${sidebarCollapsed ? 'justify-center' : ''}`}
                title={sidebarCollapsed ? item.label : undefined}
              >
                <Icon size={18} className="flex-shrink-0" />
                {!sidebarCollapsed && (
                  <span className="text-sm font-bold truncate">{item.label}</span>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Footer - Created by */}
        <div className="p-3 border-t border-slate-100">
          {!sidebarCollapsed ? (
            <div className="text-center">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Created by</p>
              <p className="text-xs font-black text-slate-600 mt-0.5">Ventro</p>
            </div>
          ) : (
            <div className="text-center">
              <p className="text-[9px] font-black text-slate-400">V</p>
            </div>
          )}
        </div>
      </aside>

      {/* Mobile Sidebar Overlay */}
      {mobileMenuOpen && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="fixed inset-0 bg-black/50" onClick={() => setMobileMenuOpen(false)} />
          <aside className="fixed left-0 top-0 bottom-0 w-64 bg-white shadow-xl flex flex-col">
            {/* Mobile Header */}
            <div className="h-14 flex items-center justify-between px-4 border-b border-slate-100">
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 bg-orange-500 rounded-lg flex items-center justify-center">
                  <span className="text-white font-black text-sm">N</span>
                </div>
                <span className="font-black text-slate-900 text-sm">Nordex</span>
              </div>
              <button
                onClick={() => setMobileMenuOpen(false)}
                className="p-1.5 rounded-lg hover:bg-slate-100"
              >
                <X size={20} />
              </button>
            </div>

            {/* Mobile Navigation */}
            <nav className="flex-1 p-2 space-y-1 overflow-y-auto">
              {navItems.map((item) => {
                const Icon = item.icon;
                const active = isActive(item);
                return (
                  <Link
                    key={item.path}
                    to={item.path}
                    onClick={() => setMobileMenuOpen(false)}
                    className={`flex items-center gap-3 px-3 py-2.5 rounded-xl transition-all ${
                      active
                        ? 'bg-orange-500 text-white'
                        : 'text-slate-600 hover:bg-slate-100'
                    }`}
                  >
                    <Icon size={18} />
                    <span className="text-sm font-bold">{item.label}</span>
                  </Link>
                );
              })}
            </nav>

            {/* Mobile Footer */}
            <div className="p-4 border-t border-slate-100 text-center">
              <p className="text-[9px] font-bold text-slate-400 uppercase tracking-widest">Created by</p>
              <p className="text-xs font-black text-slate-600 mt-0.5">Ventro</p>
            </div>
          </aside>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Top Header Bar */}
        <header className="h-14 bg-white border-b border-slate-200 sticky top-0 z-30">
          <div className="h-full max-w-7xl mx-auto px-8 flex items-center justify-between">
            {/* Left - Page Title (aligned with content container left edge) */}
            <div className="flex items-center gap-3">
              <button
                onClick={() => setMobileMenuOpen(true)}
                className="lg:hidden p-2 rounded-lg hover:bg-slate-100 -ml-2"
              >
                <Menu size={20} />
              </button>
              <div>
                <h1 className="text-lg font-black text-slate-900 uppercase tracking-wide">
                  {currentPage.title}
                </h1>
                <p className="text-xs text-slate-500 font-medium">
                  {currentPage.subtitle}
                </p>
              </div>
            </div>

            {/* Right - Organization & User (aligned with content container right edge) */}
            <div className="flex items-center gap-4">
              {/* Project Selector Dropdown */}
              <div className="hidden md:block relative">
                <button
                  onClick={() => setShowProjectMenu(!showProjectMenu)}
                  className="flex items-center gap-2 px-3 py-1.5 bg-orange-50 rounded-lg border border-orange-300 hover:bg-orange-100 transition-colors"
                >
                  <Globe className="h-4 w-4 text-orange-500" />
                  <span className="text-xs font-bold text-slate-700">
                    {selectedProject?.name || 'Select Project'}
                  </span>
                  <ChevronDown className={`h-3 w-3 text-orange-400 transition-transform ${showProjectMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* Project Dropdown */}
                {showProjectMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowProjectMenu(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 w-64 bg-white rounded-xl border border-slate-200 shadow-lg z-50 overflow-hidden">
                      <div className="p-2 border-b border-slate-100">
                        <p className="text-[9px] font-black text-slate-400 uppercase tracking-widest px-2">Switch Project</p>
                      </div>
                      <div className="max-h-64 overflow-y-auto p-1">
                        {projects.length === 0 ? (
                          <p className="px-3 py-2 text-xs text-slate-500">No projects available</p>
                        ) : (
                          projects.map((project) => (
                            <button
                              key={project.id}
                              onClick={() => handleProjectSelect(project)}
                              className={`w-full text-left px-3 py-2 rounded-lg text-xs transition-colors ${
                                selectedProject?.id === project.id
                                  ? 'bg-orange-50 text-orange-700 font-bold'
                                  : 'text-slate-700 hover:bg-slate-50'
                              }`}
                            >
                              <p className="font-bold truncate">{project.name}</p>
                            </button>
                          ))
                        )}
                      </div>
                      <div className="p-2 border-t border-slate-100">
                        <button
                          onClick={() => {
                            setShowProjectMenu(false);
                            navigate('/oem/projects');
                          }}
                          className="w-full px-3 py-2 text-xs font-bold text-orange-600 hover:bg-orange-50 rounded-lg transition-colors text-center"
                        >
                          View All Projects
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>

              {/* User Quick Info with Dropdown */}
              <div className="relative">
                <button
                  onClick={() => setShowUserMenu(!showUserMenu)}
                  className="flex items-center gap-2 px-2 py-1 rounded-lg hover:bg-slate-100 transition-colors"
                >
                  <div className="text-right hidden sm:block">
                    <span className="block text-xs font-bold text-slate-900">
                      {user?.firstName ? `${user.firstName.charAt(0)}. ${user.lastName || ''}` : user?.username}
                    </span>
                    <span className="block text-[10px] font-bold text-orange-500 uppercase">
                      {getUserRole()}
                    </span>
                  </div>
                  {user?.profilePicture ? (
                    <img src={user.profilePicture} alt="" className="w-8 h-8 rounded-lg object-cover" />
                  ) : (
                    <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center">
                      <span className="text-sm font-black text-orange-600">
                        {user?.firstName?.charAt(0) || 'U'}
                      </span>
                    </div>
                  )}
                  <ChevronDown className={`h-3 w-3 text-slate-400 transition-transform ${showUserMenu ? 'rotate-180' : ''}`} />
                </button>

                {/* User Dropdown */}
                {showUserMenu && (
                  <>
                    <div
                      className="fixed inset-0 z-40"
                      onClick={() => setShowUserMenu(false)}
                    />
                    <div className="absolute right-0 top-full mt-2 w-56 bg-white rounded-xl border border-slate-200 shadow-lg z-50 overflow-hidden">
                      <div className="p-3 border-b border-slate-100">
                        <p className="text-xs font-bold text-slate-900">{user?.firstName} {user?.lastName}</p>
                        <p className="text-[10px] text-slate-500 truncate">{user?.email}</p>
                      </div>
                      <div className="p-1">
                        <button
                          onClick={handleLogout}
                          className="w-full flex items-center gap-2 px-3 py-2 text-xs font-bold text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                        >
                          <LogOut size={14} />
                          Logout
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </header>

        {/* Main Content */}
        <main className="flex-1 overflow-auto">
          {children}
        </main>
      </div>
    </div>
  );
};

export default OEMLayout;
